/**
 * PINInput Component
 * 
 * Numeric PIN input with on-screen keypad for touch devices.
 * Supports keyboard input and show/hide PIN functionality.
 */

import { useState, useEffect, useRef } from 'react';

interface PINInputProps {
  onSubmit: (pin: string) => void;
  isLocked?: boolean;
  lockTimeRemaining?: number;
  error?: string | null;
  isLoading?: boolean;
}

const KEYPAD_BUTTONS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

export function PINInput({ 
  onSubmit, 
  isLocked = false, 
  lockTimeRemaining = 0,
  error = null,
  isLoading = false,
}: PINInputProps) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    if (!isLocked && !isLoading) {
      inputRef.current?.focus();
    }
  }, [isLocked, isLoading]);

  // Clear PIN when error changes (new error)
  useEffect(() => {
    if (error) {
      setPin('');
    }
  }, [error]);

  const handleKeypadPress = (key: string) => {
    if (isLocked || isLoading) return;

    if (key === 'clear') {
      setPin('');
    } else if (key === 'backspace') {
      setPin(prev => prev.slice(0, -1));
    } else if (pin.length < 6) {
      setPin(prev => prev + key);
    }
  };

  const handleKeyboardInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isLocked || isLoading) {
      e.preventDefault();
      return;
    }

    // Allow only numbers, backspace, delete, and enter
    if (e.key === 'Enter' && pin.length >= 4) {
      handleSubmit();
    } else if (e.key === 'Backspace' || e.key === 'Delete') {
      // Let the default behavior handle it
      return;
    } else if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
  };

  const handleSubmit = () => {
    if (pin.length >= 4 && pin.length <= 6 && !isLocked && !isLoading) {
      onSubmit(pin);
    }
  };

  const canSubmit = pin.length >= 4 && pin.length <= 6 && !isLocked && !isLoading;

  return (
    <div className="w-full max-w-md mx-auto">
      <h2 className="text-3xl font-bold text-center mb-1 text-gray-800">
        Enter PIN
      </h2>
      <p className="text-center text-gray-600 mb-6">
        Enter the outlet PIN to authenticate
      </p>

      {/* PIN Display */}
      <div className="mb-4">
        <div className="relative">
          <input
            ref={inputRef}
            type={showPin ? 'text' : 'password'}
            value={pin}
            onChange={handleInputChange}
            onKeyDown={handleKeyboardInput}
            disabled={isLocked || isLoading}
            maxLength={6}
            placeholder="Enter 4-6 digit PIN"
            className="w-full px-6 py-4 text-2xl text-center font-mono tracking-widest border-2 border-gray-300 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
            autoComplete="off"
            inputMode="numeric"
          />
          
          <button
            type="button"
            onClick={() => setShowPin(!showPin)}
            disabled={isLocked || isLoading}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
          >
            {showPin ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        </div>

        {/* PIN Length Indicator */}
        <div className="flex justify-center gap-2 mt-3">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className={`w-3 h-3 rounded-full transition-all ${
                i < pin.length
                  ? 'bg-purple-500 scale-110'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-center">
          <p className="text-red-600 font-semibold">{error}</p>
        </div>
      )}

      {/* Lock Message */}
      {isLocked && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
          <div className="text-3xl mb-2">🔒</div>
          <p className="text-yellow-800 font-semibold">Too many failed attempts</p>
          <p className="text-yellow-600 text-sm mt-1">
            Please wait {lockTimeRemaining} seconds before trying again
          </p>
        </div>
      )}

      {/* Numeric Keypad */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {KEYPAD_BUTTONS.flat().map((key) => {
          if (key === 'clear') {
            return (
              <button
                key={key}
                onClick={() => handleKeypadPress(key)}
                disabled={isLocked || isLoading || pin.length === 0}
                className="h-12 w-full rounded-md bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed font-medium text-gray-700 transition-all active:scale-95 flex items-center justify-center text-[11px] sm:text-xs"
              >
                <span>Clear</span>
              </button>
            );
          }
          
          if (key === 'backspace') {
            return (
              <button
                key={key}
                onClick={() => handleKeypadPress(key)}
                disabled={isLocked || isLoading || pin.length === 0}
                className="h-12 w-full rounded-md bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed font-medium text-gray-700 transition-all active:scale-95 flex items-center justify-center"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z" />
                </svg>
              </button>
            );
          }

          return (
            <button
              key={key}
              onClick={() => handleKeypadPress(key)}
              disabled={isLocked || isLoading || pin.length >= 6}
              className="h-12 w-full rounded-md bg-white border-2 border-gray-300 hover:border-purple-400 hover:bg-purple-50 disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed font-semibold text-lg text-gray-800 transition-all active:scale-95 shadow-sm hover:shadow-md"
            >
              {key}
            </button>
          );
        })}
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:shadow-none transition-all active:scale-[0.98] flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>Authenticating...</span>
          </>
        ) : (
          <>
            <span>Authenticate</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </>
        )}
      </button>

      <div className="mt-3 text-center text-sm text-gray-500">
        <p>🔐 PIN must be 4-6 digits</p>
      </div>
    </div>
  );
}
